// Deterministic content-hash cf-id derivation. Pure, synchronous (runs inside the
// reducer path, so no crypto.subtle). An id is stable under inserts/reorders of
// OTHER nodes because it depends on the node's own content + its parent-path, not
// its document position. Identical siblings are disambiguated by their index
// within the identical set keyed by that path (T3-F2): truly-identical siblings
// reordered among themselves are indistinguishable — accepted residual limit.

/** 32-bit FNV-1a → base36. Sync, fast, no deps. */
export function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** Direct (own) text of an element, normalized — children's text excluded so a
 *  child edit doesn't perturb the parent's key. */
function directText(el: Element): string {
  let t = '';
  for (const n of el.childNodes) {
    if (n.nodeType === 3 /* TEXT_NODE */) t += n.nodeValue ?? '';
  }
  return t.trim().replace(/\s+/g, ' ');
}

/** Sorted non-cf attributes as `name=value` joined — structural identity. */
function attrKey(el: Element): string {
  return Array.from(el.attributes)
    .filter((a) => a.name !== 'data-cf-id' && a.name !== 'data-cf-alias')
    .map((a) => `${a.name}=${a.value}`)
    .sort()
    .join('&');
}

// Per-element key cache. cfNodeKey is called O(siblings + ancestors) times per
// node via inSetIndex/parentPathKey, so without memoization a single frame's
// hashing is super-linear. The key excludes data-cf-id/data-cf-alias, the only
// attributes the migration mutates, so it stays valid as ids are written. Keys
// are per-parse Element instances → no cross-tree contamination; WeakMap GCs.
const keyCache = new WeakMap<Element, string>();

/** This node's own signature (tag + attrs + direct text), cf-id-independent. */
export function cfNodeKey(el: Element): string {
  const cached = keyCache.get(el);
  if (cached !== undefined) return cached;
  const key = `${el.tagName.toLowerCase()}|${attrKey(el)}|${directText(el)}`;
  keyCache.set(el, key);
  return key;
}

/** Index of `el` within the set of siblings sharing its key under the same parent
 *  (stable disambiguator for identical siblings). */
function inSetIndex(el: Element): number {
  const key = cfNodeKey(el);
  let i = 0;
  let sib = el.previousElementSibling;
  while (sib) {
    if (cfNodeKey(sib) === key) i++;
    sib = sib.previousElementSibling;
  }
  return i;
}

/** Path key from the document/root down to `el`'s parent: each ancestor's key +
 *  its in-set index, so two same-signature nodes under different ancestors differ.
 *  Stops at the parse host (`<div>` wrapper) or document body. */
function parentPathKey(el: Element): string {
  const parts: string[] = [];
  let p = el.parentElement;
  while (p && p.tagName.toLowerCase() !== 'body' && p.parentElement) {
    parts.push(`${cfNodeKey(p)}#${inSetIndex(p)}`);
    p = p.parentElement;
  }
  return parts.reverse().join('>');
}

/** The content-hash id for `el`: `cf-<hash>` over its key + path + in-set index. */
export function cfHashId(el: Element): string {
  const key = `${parentPathKey(el)}>${cfNodeKey(el)}#${inSetIndex(el)}`;
  return `cf-${fnv1a(key)}`;
}
